
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Modal, Button, Typography, Steps, Icon, message } from "antd";
import WrappedCustomerData from "./CustomerData";
import WrappedVehicleData from "./VehicleData";
import WrappedPaymentData from "./PaymentData";
import WrappedDownloadData from "./DownloadData";

const { Step } = Steps;
const { Text } = Typography;

// Move icons outside component to prevent recreation on every render
const ICONS = [
  <Icon key={1} style={{ fontSize: "1rem" }} type="user" />,
  <Icon key={2} style={{ fontSize: "1rem" }} type="car" />,
  <Icon key={3} style={{ fontSize: "1rem" }} type="transaction" />,
  <Icon key={4} style={{ fontSize: "1rem" }} type="upload" />,
  <Icon key={5} style={{ fontSize: "1rem" }} type="check" />,
];

// default data structure
const defaultData = {
  bookingId: null,
  customer: null,
  customerId: null,
  customerName: null,
  customerFatherName: null,
  customerAddress: null,
  customerPhone: null,
  customerLocality: null,
  customerCountry: null,
  customerState: null,
  customerCity: null,
  customerPincode: null,
  customerEmail: null,
  customerDob: null,
  customerGender: null,
  IDbranch: null,
  nomineeName: null,
  nomineeAge: null,
  relationship: null,
  vehicle: null,
  rto: null,
  rtoCode: null,
  rtoArea: null,
  exchangeVehicleName: null,
  exchangeVehiclePrice: null,
  price: null,
  accessories: [],
  quotation: [],
  color: null,
  loan: null,
  authentication: null,
  pdf: "example",
  pdfSigned: "example",
  executive: null,
  remarks: null,
  bookingStatus: null,
  authorisedTime: null,
  refferedBy: null,
  customerPancard: null,
  customerAadharCard: null,
};

const BookingRegisterModal = (props) => {
  const {
    open,
    close,
    id,
    currentData,
    editable,
    emitData,
    clearBookingData,
    setBFlag,
    bFlag,
    mode,
    isSales,
    phone,
  } = props;

  const [currentBookingData, setCurrentBookingData] = useState(
    isSales ? currentData : defaultData
  );
  const [customerId, setCustomerId] = useState("");
  const [customerAvailable, setCustomerAvailable] = useState(false);

  const ChildRef = useRef();
  const ClearRef = useRef();
  const [current, setCurrent] = useState(0);

  // Memoize customer ID extraction
  const customerIdFromParent = useMemo(() => {
    return (
      currentData?.customer?.id ||
      currentData?.customerId ||
      currentData?.customer?.customerId
    );
  }, [currentData]);

  useEffect(() => {
    if (currentData && open) {
      let accessoryArray = [];

      let temp = {
        ...currentData,
        customer: currentData.customer || {
          id: currentData.customerId || currentData.customer?.id,
          customerId: currentData.customerId || currentData.customer?.customerId,
          name: currentData.customerName || currentData.customer?.name,
        },
        quotation: currentData.quotation || currentData.Quotation || [],
      };

      if (temp?.accessories?.length > 0) {
        temp.accessories.forEach((data) => {
          let obj = { ...data };
          if (data.accessory) {
            obj = {
              ...data.accessory,
              quantity: data.quantity,
              discount: data.discount,
              isPercent: data.isPercent && data.isPercent,
              arrayId: data.id || "",
            };
          }
          accessoryArray.push(obj);
        });
      }

      temp.accessories = accessoryArray;

      console.log("Initialized booking data with customer:", {
        id: temp.customer?.id,
        customerId: temp.customer?.customerId,
      });

      setCurrentBookingData(temp);
    } else if (!open) {
      setCurrentBookingData(isSales ? currentData : defaultData);
      setCurrent(0);
      setCustomerId("");
      setCustomerAvailable(false);
    }
  }, [open, currentData, isSales]);

  // Memoize steps array
  const steps = useMemo(() => {
    const baseSteps = [
      { id: 1, title: "Customer Data" },
      { id: 2, title: "Vehicle Data" },
      { id: 3, title: "Payment Data" },
    ];
    if (mode === "ConfirmBooking") {
      baseSteps.push({ id: 4, title: "Customer Authentication" });
    }
    return baseSteps;
  }, [mode]);

  const handleSetCustomerId = useCallback((data) => {
    setCustomerId(data);
  }, []);

  const reformatSelectedVehicle = useCallback((data) => {
    if (data?.selectedVehicle) {
      return {
        selectedVehicle: data.selectedVehicle.map((vehicle) => {
          const { color, vehicleDetail } = vehicle;
          if (!color) return { color: null, vehicleDetail };
          const imageDetails = color?.imageDetails?.[0];
          return {
            color: {
              id: color?.colorId ?? color?.id ?? null,
              color: color?.colorName ?? color?.color ?? null,
              code: imageDetails?.code ?? color?.code ?? null,
              url: imageDetails?.url ?? color?.url ?? null,
            },
            vehicleDetail,
          };
        }),
      };
    }
    return null;
  }, []);

  /**
   * buildPayload
   * Assembles the full booking payload from currentBookingData.
   * Extracted so both handleSubmit and handleSubmitFinal can reuse it.
   */
  const buildPayload = useCallback(() => {
    const reformattedVehicle = reformatSelectedVehicle(currentBookingData);
    const data = {
      ...currentBookingData,
      selectedVehicle: reformattedVehicle?.selectedVehicle,
    };
    data.bookingId = id;

    console.log("Customer data before submission:", {
      customer: data.customer,
      customerId: data.customerId,
      customerName: data.customerName,
    });

    let customerObject = data.customer;
    if (!customerObject || !customerObject.id) {
      customerObject = {
        id: data.customer?.id || data.customerId,
        customerId: data.customer?.customerId || data.customerId,
        name: data.customer?.name || data.customerName,
        ...data.customer,
      };
      console.log("Reconstructed customer object:", customerObject);
    }

    let accesoriesArray = [];
    if (data?.accessories?.length > 0) {
      data.accessories.forEach((obj, index) => {
        let newObj;
        if (
          currentBookingData?.accessories?.[index] &&
          "accessory" in currentBookingData.accessories[index]
        ) {
          newObj = { ...obj };
        } else {
          newObj = {
            id: obj.arrayId || "",
            accessory: {
              id: obj?.id,
              name: obj?.partName,
              price: obj?.mrp,
            },
            discount: obj?.discount,
            isPercent: obj?.isPercent && obj?.isPercent,
            quantity: obj?.quantity,
          };
        }
        accesoriesArray.push(newObj);
      });
    }

    let quotationArr = [];
    if (currentBookingData?.quotation?.length > 0) {
      currentBookingData.quotation.forEach((quotationData) => {
        if (quotationData.id) {
          quotationArr.push({ id: quotationData.id });
        } else {
          quotationArr.push({ id: quotationData });
        }
      });
    }

    let payload = {
      isAdvanceBooking: mode === "ConfirmBooking" ? false : true,
      bookingId: data?.bookingId ?? id,
      confirmBookingId: data?.confirmBookingId,
      customer: customerObject,
      customerId: customerObject?.customerId || data.customerId,
      customerName: customerObject?.name || data.customerName,
      customerFatherName: data?.customerFatherName,
      customerSalutation: data?.customerSalutation,
      customerAddress: data?.customerAddress,
      customerLine1: data?.customerLine1,
      customerLine2: data?.customerLine2,
      customerLine3: data?.customerLine3,
      customerPhone: data?.customerPhone,
      customerGender: data?.customerGender,
      customerLocality: data?.customerLocality,
      customerCountry: data?.customerCountry,
      customerState: data?.customerState,
      customerCity: data?.customerCity,
      customerPincode: data?.customerPincode,
      customerEmail: data?.customerEmail,
      customerDob: data?.customerDob,
      customerPancard: data?.customerPancard,
      customerAadharCard: data?.customerAadharCard,
      IDbranch: data?.IDbranch,
      branch: Array.isArray(data?.branch) ? data.branch[0] : data?.branch,
      nomineeName: data?.nomineeName,
      nomineeAge: data?.nomineeAge,
      relationship: data?.relationship,
      vehicle: data?.vehicle,
      rto: data?.rto,
      price: {
        onRoadDiscount: parseFloat(data?.price?.onRoadDiscount) || 0,
        onRoadPrice: parseFloat(data?.price?.onRoadPrice) || 0,
        showroomPrice:
          data?.vehiclePrice?.showroomPrice || data?.price?.showroomPrice || 0,
        insuranceAmount:
          data?.insuranceAmount || data?.price?.insuranceAmount || 0,
        roadTax: data?.vehiclePrice?.roadTax || data?.price?.roadTax || 0,
        warrantyPrice:
          data?.vehiclePrice?.warrantyPrice || data?.price?.warrantyPrice || 0,
        registrationFee:
          data?.vehiclePrice?.registrationFee ||
          data?.price?.registrationFee ||
          0,
        handlingCharges:
          data?.vehiclePrice?.handlingCharges ||
          data?.price?.handlingCharges ||
          0,
        insuranceType: data?.price?.insuranceType,
        numberPlate: parseFloat(data?.price?.numberPlate) || 0,
        tempRegister: parseFloat(data?.price?.tempRegister) || 0,
        hp: parseFloat(data?.price?.hp) || 0,
        paymentMode: data?.price?.paymentMode,
        affidavit: parseFloat(data?.price?.affidavit) || 0,
        netRecieveables: parseFloat(data?.price?.netRecieveables) || 0,
        specialNoCharges: parseFloat(data?.price?.specialNoCharges) || 0,
        accessoriesTotal: parseFloat(data?.price?.accessoriesTotal) || 0,
        accessoriesTotalAfterDiscount:
          parseFloat(data?.price?.accessoriesTotalAfterDiscount) || 0,
        totalDiscount: parseFloat(data?.price?.totalDiscount) || 0,
        finalAmount: parseFloat(data?.price?.finalAmount) || 0,
      },
      priceData: {
        onRoadDiscount: parseFloat(data?.price?.onRoadDiscount) || 0,
        onRoadPrice: parseFloat(data?.price?.onRoadPrice) || 0,
        insuranceType: data?.price?.insuranceType,
        numberPlate: parseFloat(data?.price?.numberPlate) || 0,
        tempRegister: parseFloat(data?.price?.tempRegister) || 0,
        handlingCharges: parseFloat(data?.price?.handlingCharges) || 0,
        hp: parseFloat(data?.price?.hp) || 0,
        paymentMode: data?.price?.paymentMode,
        affidavit: parseFloat(data?.price?.affidavit) || 0,
        netRecieveables: parseFloat(data?.price?.netRecieveables) || 0,
        specialNoCharges: parseFloat(data?.price?.specialNoCharges) || 0,
        accessoriesTotal: parseFloat(data?.price?.accessoriesTotal) || 0,
        accessoriesTotalAfterDiscount:
          parseFloat(data?.price?.accessoriesTotalAfterDiscount) || 0,
        totalDiscount: parseFloat(data?.price?.totalDiscount) || 0,
        finalAmount: parseFloat(data?.price?.finalAmount) || 0,
      },
      remarks: data?.remarks,
      bhSeries: data?.bhSeries ?? null,
      specialNo: data?.specialNo ?? null,
      specialNoRemarks: data?.specialNo ? (data?.specialNoRemarks ?? null) : null,
      rtoCode: data?.rtoCode,
      rtoArea: data?.rtoArea,
      exchangeVehicleName: data?.exchangeVehicleName,
      exchangeVehiclePrice: data?.exchangeVehiclePrice,
      accessories: accesoriesArray,
      quotation: quotationArr,
      color: { id: data?.selectedVehicle?.[0]?.color?.id },
      loan: {
        hypothecation: data?.loan?.hypothecation,
        financerBranch: data?.loan?.financerBranch,
        financer: data?.loan?.financer?.id ? data?.loan?.financer : null,
        downPayment: parseInt(data?.loan?.downPayment) || 0,
        loanAmount: parseInt(data?.loan?.loanAmount) || 0,
        tenure: parseInt(data?.loan?.tenure) || 0,
        emiDate: parseInt(data?.loan?.emiDate) || 0,
        emiStartDate: data?.loan?.emiStartDate,
        loanType: data?.loan?.loanType,
        emiAmount: parseFloat(data?.loan?.emiAmount) || 0,
        disbursementAmount: parseFloat(data?.loan?.disbursementAmount) || 0,
        showroomFinanceCharges:
          parseFloat(data?.loan?.showroomFinanceCharges) || 0,
      },
      loanData: {
        finName: data?.finName,
        financerBranch: data?.loan?.financerBranch,
        downPayment: parseInt(data?.loan?.downPayment) || 0,
        loanAmount: parseInt(data?.loan?.loanAmount) || 0,
        tenure: parseInt(data?.loan?.tenure) || 0,
        emiDate: parseInt(data?.loan?.emiDate) || 0,
        emiStartDate: data?.loan?.emiStartDate,
        loanType: data?.loan?.loanType,
        emiAmount: parseFloat(data?.loan?.emiAmount) || 0,
        disbursementAmount: parseFloat(data?.loan?.disbursementAmount) || 0,
        showroomFinanceCharges:
          parseFloat(data?.loan?.showroomFinanceCharges) || 0,
      },
      selectedVehicle: data?.selectedVehicle,
      authentication: {
        beforeVerification: data?.authentication?.beforeVerification ?? null,
        afterVerification: data?.authentication?.afterVerification ?? null,
        digital: data?.authentication?.digital ?? null,
        verifiedAt: data?.authentication?.verifiedAt ?? null,
      },
      exchange: {
        vehicleModel: data?.exchange?.vehicleModel ?? null,
        vehiclePrice:
          data?.exchange?.vehiclePrice !== undefined &&
          data?.exchange?.vehiclePrice !== null &&
          data?.exchange?.vehiclePrice !== ""
            ? parseFloat(data?.exchange?.vehiclePrice)
            : null,
      },
      pdf: "example",
      pdfSigned: "example",
      executive: data?.executive,
      bookingStatus:
        mode === "ConfirmBooking"
          ? data?.bookingStatus || "PENDING"
          : "ADVANCE",
      authorisedTime: data?.authorisedTime || null,
      refferedBy: data?.refferedBy,
      // ── expectedDeliveryDate resolution order ────────────────────────────
      // 1. booking root (set by VehicleData.validatePage)
      // 2. selectedVehicle[0].expectedDeliveryDate (set by addVehicle, kept in sync)
      // 3. null
      expectedDeliveryDate:
        data?.expectedDeliveryDate ||
        data?.selectedVehicle?.[0]?.expectedDeliveryDate ||
        null,
    };

    console.log("Final payload customer:", {
      id: payload.customer?.id,
      customerId: payload.customer?.customerId,
      name: payload.customer?.name,
    });

    if (data?.id) payload.id = data.id;

    return payload;
  }, [currentBookingData, id, mode, reformatSelectedVehicle]);

  /**
   * handleSubmit
   *
   * Called when the Next / Save & Continue button is clicked.
   *
   * For Confirm Booking (mode === "ConfirmBooking"):
   *   - Steps 0, 1  → just advance the tab (no API)
   *   - Step  2     → Payment step: call emitData with skipApi=true (store data only, no API call)
   *                   then advance to Step 3 (Authentication)
   *   - Step  3     → Authentication step: call emitData with skipApi=false (real API call)
   *                   then clearFields / close
   *
   * For Advanced Booking (mode === "AdvancedBooking"):
   *   - Steps 0, 1  → just advance the tab
   *   - Step  2     → Payment step: call emitData with skipApi=false (real API call, booking saved)
   *                   then clearFields / close
   */
  const handleSubmit = useCallback(
    (changeTab = false) => {
      const payload = buildPayload();

      if (mode === "ConfirmBooking") {
        if (current === 2) {
          // ── Payment step for Confirm Booking ──
          // Store data locally (skipApi = true), then advance to Authentication step.
          emitData(payload, false, true /* skipApi */);
          setCurrent((prev) => prev + 1);
        } else if (current === 3) {
          // ── Authentication step for Confirm Booking ──
          // Real API call (skipApi = false), then close.
          emitData(payload, false, false);
          clearFields();
        } else {
          // Steps 0 and 1 – just move forward
          if (changeTab) setCurrent((prev) => prev + 1);
        }
      } else {
        // Advanced Booking
        if (current === 2) {
          // ── Payment step for Advanced Booking ──
          // Real API call (skipApi = false), then close.
          emitData(payload, false, false);
          clearFields();
        } else {
          // Steps 0 and 1 – just move forward
          if (changeTab) setCurrent((prev) => prev + 1);
        }
      }
    },
    [buildPayload, current, mode, emitData]
  );

  // Memoize forms array
  const forms = useMemo(() => {
    const baseForms = [
      <WrappedCustomerData
        mode={mode}
        key={1}
        data={currentBookingData}
        isSales={isSales}
        open={open}
        validityRef={ChildRef}
        clearRef={ClearRef}
        editable={editable}
        setCustomerId={handleSetCustomerId}
        customerAvailable={customerAvailable}
        setCustomerAvailable={setCustomerAvailable}
        customerId={customerId}
        customerIdFromParent={customerIdFromParent}
        phone={phone}
        setCurrentBookingData={setCurrentBookingData}
      />,
      <WrappedVehicleData
        mode={mode}
        key={2}
        isSales={isSales}
        data={currentBookingData}
        setCurrentBookingData={setCurrentBookingData}  // lets VehicleData push date back up
        open={open}
        validityRef={ChildRef}
        clearRef={ClearRef}
        editable={editable}
      />,
      <WrappedPaymentData
        mode={mode}
        key={3}
        isSales={isSales}
        data={currentBookingData}
        open={open}
        validityRef={ChildRef}
        clearRef={ClearRef}
        editable={editable}
      />,
    ];

    if (mode === "ConfirmBooking") {
      baseForms.push(
        <WrappedDownloadData
          key={4}
          id={id}
          isSales={isSales}
          data={currentBookingData}
          open={open}
          validityRef={ChildRef}
          clearRef={ClearRef}
          editable={editable}
          setBFlag={setBFlag}
          bFlag={bFlag}
          // saveHere triggers the final API call from inside DownloadData (OTP verify path)
          saveHere={() => {
            const payload = buildPayload();
            emitData(payload, false, false);
          }}
        />
      );
    }

    return baseForms;
  }, [
    mode,
    currentBookingData,
    isSales,
    open,
    editable,
    handleSetCustomerId,
    customerAvailable,
    customerId,
    customerIdFromParent,
    id,
    bFlag,
    setBFlag,
    buildPayload,
    emitData,
  ]);

  // Memoize clearFields function
  const clearFields = useCallback(() => {
    clearBookingData(defaultData);
    setCurrentBookingData(defaultData);
    setCurrent(0);
    setCustomerId("");
    if (ClearRef.current) ClearRef.current();
    setCustomerAvailable(false);
    close();
  }, [clearBookingData, close]);

  // ── Footer ────────────────────────────────────────────────────────────────
  const footer = useMemo(() => {
    if (editable) {
      return [
        <Button
          key="back"
          onClick={() => {
            if (current > 0) {
              setCurrent(current - 1);
            } else {
              close();
              clearFields();
            }
          }}
        >
          {current > 0 ? "Back" : "Cancel"}
        </Button>,
        <Button
          key="next"
          loading={bFlag}
          disabled={bFlag || !customerId}
          onClick={() => {
            if (ChildRef.current && ChildRef.current()) {
              if (mode === "ConfirmBooking") {
                if (current !== 2) {
                  if (current === 3) {
                    // Authentication step – final save
                    handleSubmit(false);
                  } else {
                    setCurrent(current + 1);
                  }
                } else {
                  // Payment step – store only, advance
                  handleSubmit(true);
                }
              } else {
                // Advanced Booking
                if (current === 2) {
                  // Payment step – final save
                  handleSubmit(false);
                } else {
                  setCurrent(current + 1);
                }
              }
            }
          }}
          type="primary"
        >
          {mode === "ConfirmBooking"
            ? current === 3
              ? "Finish"
              : current === 2
                ? "Save & Continue"
                : "Next"
            : current === 2
              ? "Save & Finish"
              : "Next"}
        </Button>,
      ];
    } else {
      return [
        <Button
          key="back"
          onClick={() => {
            if (current > 0) {
              setCurrent(current - 1);
            } else {
              close();
            }
          }}
        >
          {current > 0 ? "Back" : "Cancel"}
        </Button>,
        <Button
          key="next"
          onClick={() => {
            if (ChildRef.current && ChildRef.current()) {
              if (mode === "ConfirmBooking") {
                if (current < 3) {
                  setCurrent(current + 1);
                } else {
                  clearFields();
                  close();
                }
              } else {
                if (current < 2) {
                  setCurrent(current + 1);
                } else {
                  clearFields();
                  close();
                }
              }
            }
          }}
          type="primary"
        >
          {mode === "ConfirmBooking"
            ? current < 3
              ? "Next"
              : "Cancel"
            : current < 2
              ? "Next"
              : "Cancel"}
        </Button>,
      ];
    }
  }, [
    editable,
    current,
    bFlag,
    customerId,
    mode,
    close,
    clearFields,
    handleSubmit,
  ]);

  return (
    <Modal
      width="80%"
      title={isSales ? "Sales Register" : "Booking Register"}
      visible={open}
      onCancel={() => {
        clearFields();
        close();
      }}
      footer={footer}
    >
      <div style={{ display: "flex", justifyContent: "space-between", margin: "0" }}>
        <Text strong>
          {isSales ? "Sale ID" : "Booking ID"}&nbsp;&nbsp;&nbsp;:{" "}
          {isSales ? currentData?.saleId || null : currentData?.bookingId}
        </Text>
        <Text strong>
          {mode && mode.replace(/([A-Z])/g, " $1").trim()}
        </Text>
      </div>
      <Text strong>Customer ID&nbsp;: {customerId}</Text>
      <br />
      <Steps size="small" type="navigation" current={current}>
        {steps.map((step, index) => (
          <Step
            key={step.id}
            icon={ICONS[index]}
            title={<Text>{step.title}</Text>}
          />
        ))}
      </Steps>
      <div>{forms[current]}</div>
    </Modal>
  );
};

export default BookingRegisterModal;